import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { ArrowLeft, Eye, EyeOff, ImageOff, Loader2, Save, Send, Trash2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../../../contexts/AuthContext';
import { storage } from '../../../firebase/config';
import {
  createNews,
  deleteNews,
  getNews,
  isSlugTaken,
  NEWS_STATUS,
  publishNews,
  slugify,
  unpublishNews,
  updateNews,
} from '../../../database/news';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import PageHeader from '../shell/PageHeader';
import NewsBlockEditor from './NewsBlockEditor';
import NewsRenderer from '../../News/NewsRenderer';

const emptyDraft = {
  title: '',
  slug: '',
  blocks: [],
  coverImage: null,
  status: NEWS_STATUS.DRAFT,
};

const NewsEditor = () => {
  const { id: routeId } = useParams();
  const isNew = !routeId;
  const navigate = useNavigate();
  const { currentUser, hasPermission } = useContext(AuthContext);
  const canManage = hasPermission('news.manage');

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploadingBlockId, setUploadingBlockId] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [preview, setPreview] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [id, setId] = useState(routeId || null);
  const [doc, setDoc] = useState(emptyDraft);
  const [slugTouched, setSlugTouched] = useState(!isNew);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const data = await getNews(routeId);
        if (!data) {
          toast.error('News not found.');
          navigate('/admin/news');
          return;
        }
        setDoc({
          title: data.title || '',
          slug: data.slug || '',
          blocks: data.blocks || [],
          coverImage: data.coverImage || null,
          status: data.status || NEWS_STATUS.DRAFT,
        });
      } catch (err) {
        toast.error('Could not load news.', { description: err.message });
      } finally {
        setLoading(false);
      }
    })();
  }, [isNew, routeId, navigate]);

  useEffect(() => {
    if (slugTouched) return;
    setDoc((d) => ({ ...d, slug: slugify(d.title) }));
  }, [doc.title, slugTouched]);

  const isPublished = doc.status === NEWS_STATUS.PUBLISHED;

  const breadcrumbs = useMemo(
    () => [
      { label: 'News', to: '/admin/news' },
      { label: isNew ? 'New' : doc.title || 'Untitled' },
    ],
    [isNew, doc.title]
  );

  if (!canManage) {
    return (
      <div className="py-20 text-center text-sm text-muted-foreground">
        You need content admin access to edit news.
      </div>
    );
  }

  const uploadToStorage = async (file, pathPrefix) => {
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${pathPrefix}/${Date.now()}_${safeName}`;
    const sref = ref(storage, path);
    await uploadBytes(sref, file);
    const url = await getDownloadURL(sref);
    return { url, path };
  };

  const ensureId = async () => {
    if (id) return id;
    const newId = await createNews({
      authorId: currentUser.uid,
      title: doc.title,
      slug: doc.slug,
      blocks: doc.blocks,
    });
    setId(newId);
    return newId;
  };

  const handleUploadBlock = async (blockId, file) => {
    try {
      setUploadingBlockId(blockId);
      const targetId = await ensureId();
      const { url, path } = await uploadToStorage(file, `news/${targetId}/blocks`);
      const existing = doc.blocks.find((b) => b.id === blockId);
      if (existing?.storagePath) {
        try {
          await deleteObject(ref(storage, existing.storagePath));
        } catch {
          /* non-fatal */
        }
      }
      setDoc((d) => ({
        ...d,
        blocks: d.blocks.map((b) => (b.id === blockId ? { ...b, url, storagePath: path } : b)),
      }));
    } catch (err) {
      toast.error('Upload failed.', { description: err.message });
    } finally {
      setUploadingBlockId(null);
    }
  };

  const handleUploadCover = async (file) => {
    try {
      setUploadingCover(true);
      const targetId = await ensureId();
      const { url, path } = await uploadToStorage(file, `news/${targetId}/cover`);
      if (doc.coverImage?.storagePath) {
        try {
          await deleteObject(ref(storage, doc.coverImage.storagePath));
        } catch {
          /* non-fatal */
        }
      }
      setDoc((d) => ({ ...d, coverImage: { url, storagePath: path } }));
    } catch (err) {
      toast.error('Cover upload failed.', { description: err.message });
    } finally {
      setUploadingCover(false);
    }
  };

  const validate = async ({ requireSlug }) => {
    if (!doc.title.trim()) {
      toast.error('Title is required.');
      return false;
    }
    if (requireSlug) {
      if (!doc.slug.trim()) {
        toast.error('Slug is required to publish.');
        return false;
      }
      if (!/^[a-z0-9-]+$/.test(doc.slug)) {
        toast.error('Slug can only contain lowercase letters, numbers, and dashes.');
        return false;
      }
      if (await isSlugTaken(doc.slug, id)) {
        toast.error('Slug is already used by another post.');
        return false;
      }
    }
    return true;
  };

  const persist = async () => {
    const patch = {
      title: doc.title.trim(),
      slug: doc.slug.trim(),
      blocks: doc.blocks,
      coverImage: doc.coverImage,
    };
    if (id) {
      await updateNews(id, patch);
      return id;
    }
    const newId = await createNews({
      authorId: currentUser.uid,
      ...patch,
    });
    setId(newId);
    return newId;
  };

  const handleSave = async () => {
    if (!(await validate({ requireSlug: false }))) return;
    setSaving(true);
    try {
      const savedId = await persist();
      toast.success('Draft saved.');
      if (isNew) navigate(`/admin/news/${savedId}`, { replace: true });
    } catch (err) {
      toast.error('Save failed.', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!(await validate({ requireSlug: true }))) return;
    setPublishing(true);
    try {
      const savedId = await persist();
      await publishNews(savedId);
      setDoc((d) => ({ ...d, status: NEWS_STATUS.PUBLISHED }));
      toast.success('Published.');
      if (isNew) navigate(`/admin/news/${savedId}`, { replace: true });
    } catch (err) {
      toast.error('Publish failed.', { description: err.message });
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!id) return;
    setPublishing(true);
    try {
      await unpublishNews(id);
      setDoc((d) => ({ ...d, status: NEWS_STATUS.DRAFT }));
      toast.success('Moved back to draft.');
    } catch (err) {
      toast.error('Could not unpublish.', { description: err.message });
    } finally {
      setPublishing(false);
    }
  };

  const handleDelete = async () => {
    if (!id) {
      navigate('/admin/news');
      return;
    }
    setDeleting(true);
    try {
      await deleteNews(id);
      toast.success('News deleted.');
      navigate('/admin/news');
    } catch (err) {
      toast.error('Delete failed.', { description: err.message });
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  if (loading) {
    return (
      <div className="pb-16">
        <PageHeader eyebrow="News" title="Loading…" breadcrumbs={breadcrumbs} />
      </div>
    );
  }

  return (
    <div className="pb-16">
      <PageHeader
        eyebrow={isPublished ? 'Published' : 'Draft'}
        title={isNew ? 'New news post' : doc.title || 'Untitled post'}
        description="Arrange text and image blocks, preview, then publish when ready."
        breadcrumbs={breadcrumbs}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/admin/news')}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <Button variant="secondary" onClick={() => setPreview((p) => !p)}>
              {preview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {preview ? 'Edit' : 'Preview'}
            </Button>
            <Button variant="secondary" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Saving…' : 'Save draft'}
            </Button>
            {isPublished ? (
              <Button
                variant="outline"
                onClick={handleUnpublish}
                disabled={publishing}
                title="Move back to draft"
              >
                <Undo2 className="h-4 w-4" />
                Unpublish
              </Button>
            ) : (
              <Button onClick={handlePublish} disabled={publishing}>
                {publishing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {publishing ? 'Publishing…' : 'Publish'}
              </Button>
            )}
            {!isNew && (
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        }
      />

      {preview ? (
        <Card className="border-border/60 bg-background/40">
          <CardContent className="px-6 py-8 sm:px-10 sm:py-12">
            {doc.coverImage?.url && (
              <img
                src={doc.coverImage.url}
                alt=""
                className="mb-8 max-h-96 w-full rounded-md border border-border/60 object-cover"
              />
            )}
            <h1 className="font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
              {doc.title || 'Untitled post'}
            </h1>
            <div className="mt-8">
              <NewsRenderer blocks={doc.blocks} />
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          <Card className="border-border/60 bg-background/40">
            <CardHeader>
              <CardTitle className="font-display text-lg">Meta</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-5">
              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
                  Title
                </span>
                <Input
                  value={doc.title}
                  onChange={(e) => setDoc((d) => ({ ...d, title: e.target.value }))}
                  placeholder="Season 5 kickoff announcement"
                />
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
                  Slug
                </span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">/news/</span>
                  <Input
                    value={doc.slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setDoc((d) => ({ ...d, slug: slugify(e.target.value) }));
                    }}
                    placeholder="season-5-kickoff"
                  />
                </div>
                <span className="text-xs text-muted-foreground">
                  Lowercase letters, numbers, and dashes. Must be unique.
                </span>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
                  Cover image
                </span>
                {doc.coverImage?.url ? (
                  <div className="space-y-2">
                    <img
                      src={doc.coverImage.url}
                      alt=""
                      className="max-h-64 w-full rounded-md border border-border/60 object-cover"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDoc((d) => ({ ...d, coverImage: null }))}
                    >
                      <ImageOff className="h-3.5 w-3.5" />
                      Remove cover
                    </Button>
                  </div>
                ) : (
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploadingCover}
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f) await handleUploadCover(f);
                    }}
                    className="block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:text-xs file:font-medium file:uppercase file:tracking-[0.15em] file:text-secondary-foreground hover:file:bg-secondary/80"
                  />
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60 bg-background/40">
            <CardHeader>
              <CardTitle className="font-display text-lg">Content</CardTitle>
            </CardHeader>
            <CardContent>
              <NewsBlockEditor
                blocks={doc.blocks}
                onChange={(blocks) => setDoc((d) => ({ ...d, blocks }))}
                onUploadImage={handleUploadBlock}
                uploadingBlockId={uploadingBlockId}
              />
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this post?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This permanently removes the news post and its blocks. Uploaded images remain in storage
            until manually cleaned.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewsEditor;
